import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "State/store";
import {
  syncCartWithServer,
  mergeWithGuestCart,
  clearCartOnLogout,
  setCartError,
} from "State/CartSlice/CartSlice";
import { useLazyGetAllCartWithIdQuery } from "Services/CartApiSlice";
import { Cart } from "Types/Cart";
import { fetchWithRetry, isNetworkError } from "Utilities/network";

export const useCartSync = (skip: boolean = false) => {
  const dispatch: AppDispatch = useDispatch();
  const token = useSelector((state: RootState) => state.login.token);
  const cartList = useSelector((state: RootState) => state.cart.cartList);
  const [fetchServerCart] = useLazyGetAllCartWithIdQuery();

  const cartListRef = useRef(cartList);
  useEffect(() => {
    cartListRef.current = cartList;
  }, [cartList]);

  const syncAttemptRef = useRef(0);
  const isSyncingRef = useRef(false);
  const lastSyncTimeRef = useRef<number>(0);
  const SYNC_COOLDOWN = 5000;

  const syncCart = useCallback(async () => {
    // Skip syncing when explicitly told to (e.g. MiniCartSidebar is closed)
    if (skip) return;

    const now = Date.now();

    if (now - lastSyncTimeRef.current < SYNC_COOLDOWN) {
      return;
    }

    if (isSyncingRef.current) {
      return;
    }

    isSyncingRef.current = true;
    syncAttemptRef.current += 1;
    lastSyncTimeRef.current = now;

    try {
      const currentCartList = cartListRef.current;
      if (token) {
        const productIds = currentCartList.map((item) => item.productId);

        if (productIds.length === 0) {
          isSyncingRef.current = false;
          return;
        }

        const response = await fetchWithRetry(
          () => fetchServerCart({ cartItems: productIds }).unwrap(),
          {
            maxRetries: 2,
            retryDelay: 1000,
          },
        );

        if (response && response.products) {
          const serverCartItems: Cart[] = response.products.map(
            (productMeta) => {
              const existingCartItem = currentCartList.find(
                (item) => item.productId === productMeta.product.item,
              );

              const availableQuantity = productMeta.product.availqty || 0;
              const localQty = existingCartItem?.quantity || 1;
              const cappedQty =
                availableQuantity > 0
                  ? Math.min(localQty, availableQuantity)
                  : localQty;

              return {
                productId: productMeta.product.item,
                quantity: cappedQty,
                availableQuantity: availableQuantity,
                ecomUnitPrice: productMeta.product.ecomUnitPrice || 0,
                discount: existingCartItem?.discount || 0,
                size: existingCartItem?.size || "",
              };
            },
          );

          dispatch(mergeWithGuestCart(serverCartItems));
          dispatch(setCartError(null));
        }
      } else {
        const storedCart = localStorage.getItem("cart");
        if (storedCart) {
          try {
            const guestCart: Cart[] = JSON.parse(storedCart);
            dispatch(syncCartWithServer(guestCart));
          } catch (e) {
            console.error("Failed to parse stored cart:", e);
            localStorage.removeItem("cart");
            dispatch(clearCartOnLogout());
          }
        } else {
          dispatch(clearCartOnLogout());
        }
      }

      syncAttemptRef.current = 0;
    } catch (error: any) {
      console.error("Failed to sync cart after all retries:", error);

      if (isNetworkError(error)) {
        if (syncAttemptRef.current === 1) {
          dispatch(
            setCartError({
              message: "Network connection issue",
              type: "network",
              timestamp: Date.now(),
            }),
          );
        }

        const storedCart = localStorage.getItem("cart");
        if (storedCart && !token) {
          try {
            const guestCart: Cart[] = JSON.parse(storedCart);
            dispatch(syncCartWithServer(guestCart));
          } catch (e) {
            // Error parsing, ignore
          }
        }
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [token, dispatch, fetchServerCart, skip]);

  useEffect(() => {
    if (skip) return;
    
    syncCart();

    
    const timer = setTimeout(() => {
      syncCart();
    }, 100);

    return () => clearTimeout(timer);
  }, [syncCart, skip]);

  
  useEffect(() => {
    if (!token || skip) return;

    const intervalId = setInterval(() => {
      syncCart();
    }, 30000); 

    return () => clearInterval(intervalId);
  }, [token, syncCart, skip]);

  return null;
};

